import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

const faqs = [
  {
    q: 'What classes or grades do you offer?',
    a: 'We currently enrol students from Pre-Nursery to Grade III, offering both Cambridge academics and an optional Hifz programme.'
  },
  {
    q: 'Is Hifz compulsory?',
    a: 'No. Our Hifz Programme is optional and integrated with academic studies. It is led by qualified instructors and is available from Grade III onward.'
  },
  {
    q: 'What curriculum do you follow?',
    a: 'We follow the Cambridge curriculum, enhanced with Islamic studies, including Nazra with Tajweed, Quranic understanding and Tarbiyah (character education).'
  },
  {
    q: 'What extracurriculars are offered?',
    a: 'We offer a range of indoor and outdoor activities such as STEM, Robotics, Archery, Entrepreneurship, Calligraphy, Public Speaking and nature based learning.'
  },
  {
    q: 'Do you teach boys and girls separately?',
    a: 'In the early years, students learn in mixed gender classrooms. Segregation (if applicable) will be introduced in later grades based on Islamic guidelines and parental feedback.'
  },
  {
    q: 'What are your school timings?',
    a: 'Our hours are Monday to Friday, 8:00 AM to 1:00 PM. On Fridays, the day ends earlier to accommodate Jummah Salah.'
  },
  {
    q: 'What makes Aureus different from other schools?',
    a: 'Aureus blends Cambridge academics with strong Islamic values, hands-on learning, small classes, and a nurturing environment.'
  },
  {
    q: 'Is there an entrance test or interview for students?',
    a: 'Yes, we conduct a simple age-appropriate assessment and brief interview to understand each child’s learning level.'
  },
  {
    q: 'How do you support slow learners or children who need extra help?',
    a: 'We provide individual attention, tailored support, and regular guidance to help every child learn confidently at their pace.'
  },
  {
    q: 'How can I apply?',
    a: (
      <>
        You can apply online via the <Link to="/apply-online" className="font-semibold text-yellow-500 underline hover:text-yellow-600">link</Link> in our contact section or schedule a visit. The admissions process includes a basic assessment and parent interview.
      </>
    )
  }
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: `url(${require('../../assets/update/Faq-Hero-Image.png')})`
          }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-60" />

        <div className="relative z-10 max-w-4xl px-4 mx-auto text-center text-white">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-2 text-base font-semibold tracking-widest text-white uppercase md:text-lg"
            style={{ letterSpacing: '0.18em' }}
          >
            Questions & Answers
          </motion.p>
          <motion.h1
            className="mb-6 text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            FREQUENTLY <span className="text-yellow-400">ASKED</span>
          </motion.h1>
          <motion.p
            className="mb-8 text-xl text-gray-200 md:text-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Everything you need to know about Aureus Cambridge School
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link
              to="/not-found"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black transition-colors duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500 group"
            >
              Apply Now
              <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black transition-all duration-300 bg-yellow-400 border-2 border-yellow-400 rounded-full shadow-lg sm:w-auto hover:bg-yellow-500 hover:border-yellow-500"
            >
              Ask a Question
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16">
        <div className="max-w-3xl px-4 mx-auto">
          {faqs.map((faq, idx) => (
            <div key={idx} className="mb-6 overflow-hidden bg-white border shadow-lg rounded-2xl border-amber-100">
              <button
                className="flex items-center justify-between w-full px-6 py-5 text-left transition-colors focus:outline-none group"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                aria-expanded={openIndex === idx}
              >
                <span className="text-lg font-semibold text-blue-900 transition-colors md:text-xl group-hover:text-amber-600">
                  {faq.q}
                </span>
                <span className={`ml-4 text-2xl transition-transform duration-300 ${openIndex === idx ? 'rotate-90 text-amber-500' : 'text-blue-900'}`}>▶</span>
              </button>
              <div
                className={`px-6 pb-5 text-slate-700 text-base transition-all duration-300 ${openIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
                style={{
                  transitionProperty: 'max-height, opacity',
                }}
              >
                {faq.a}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default FAQ; 